<template>
    <div class="keyword-management-container">
        <!-- 主内容区域 -->
        <div class="main-content">
            <!-- 关键词类型切换 -->
            <div class="button-group">
                <button v-for="button in keywordTypes" :key="button.id" class="main-button"
                    :class="{ active: activeKeywordType === button.id }" @click="handleKeywordTypeChange(button.id)">
                    {{ button.label }}
                </button>
            </div>

            <!-- 操作按钮 -->
            <div class="action-buttons">
                <el-button type="primary" @click="openAddDialog" class="add-button">
                    <el-icon>
                        <Plus />
                    </el-icon>
                    添加{{ getCurrentTypeLabel() }}
                </el-button>
            </div>

            <!-- 关键词包列表 -->
            <div class="package-list">
                <el-table :data="packageList" stripe style="width: 100%" v-loading="loading">
                    <el-table-column type="index" label="序号" width="60" />
                    <el-table-column prop="package_name" label="词包名称" width="150" />
                    <el-table-column prop="original_keyword" label="核心关键词" width="120" />
                    <el-table-column prop="keyword_count" label="关键词数量" width="100" />
                    <el-table-column prop="industry" label="行业" width="100">
                        <template #default="{ row }">
                            {{ getIndustryLabel(row.industry) }}
                        </template>
                    </el-table-column>
                    <el-table-column prop="description" label="说明" show-overflow-tooltip />
                    <el-table-column prop="create_time" label="创建时间" width="120" />
                    <el-table-column label="操作" width="200" fixed="right">
                        <template #default="scope">
                            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
                                <el-icon>
                                    <Edit />
                                </el-icon>
                                编辑
                            </el-button>
                            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
                                <el-icon>
                                    <Delete />
                                </el-icon>
                                删除
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页 -->
                <div class="pagination-container">
                    <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                        :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                        layout="total, sizes, prev, pager, next, jumper" @size-change="fetchPackageList"
                        @current-change="fetchPackageList" />
                </div>
            </div>
        </div>

        <!-- 添加/编辑关键词包对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" width="90%" top="5vh" :close-on-click-modal="false"
            class="keyword-dialog" style="max-width: 1200px;">
            <div class="dialog-content-wrapper">
                <!-- 第一行：标题和提示 -->
                <div class="dialog-header">
                    <h3 class="dialog-main-title">
                        添加{{ getCurrentTypeLabel() }}
                        <span class="dialog-subtitle">（只需填核心词即可，最多选择200个词）</span>
                        <span class="warning-text">关键词不要包含任何区域</span>
                    </h3>
                </div>

                <!-- 第二行：关键词输入和操作 -->
                <div class="keyword-input-row">
                    <el-input v-model="keywordForm.original_keyword" :placeholder="`请输入关键词(必填，最少3个字，建议填写5-7字具体业务词)`"
                        clearable class="keyword-input" />

                    <el-select v-model="keywordForm.industry" placeholder="请选择行业" class="industry-select">
                        <el-option label="制造业" value="manufacturing" />
                        <el-option label="服务业" value="service" />
                    </el-select>

                    <el-button type="primary" @click="handleDistill" :loading="distilling" class="distill-button">
                        <el-icon>
                            <Filter />
                        </el-icon>
                        蒸馏
                    </el-button>

                    <el-button @click="openManualBatchDialog" class="manual-batch-button">
                        <el-icon>
                            <DocumentAdd />
                        </el-icon>
                        手动批量
                    </el-button>
                </div>

                <!-- 第三行：说明输入 -->
                <div class="description-row">
                    <el-input v-model="keywordForm.description" placeholder="请输入说明，以方便区别词包的作用(选填)" clearable
                        class="description-input" />
                </div>

                <!-- 编辑模式下的关键词显示（不可修改） -->
                <div v-if="isEditMode" class="edit-keyword-display">
                    <div class="keyword-display">
                        <span class="label">关键词：</span>
                        <span class="value">{{ keywordForm.original_keyword }}</span>
                    </div>
                    <div class="edit-tip">关键词不可修改，需修改请联系客服</div>
                </div>

                <!-- 关键词列表 -->
                <div class="keywords-list-section">
                    <div class="list-header">
                        <div class="header-left">
                            <el-checkbox v-model="selectAll" @change="handleSelectAll">全选</el-checkbox>
                            <span class="selected-count">已选 {{ selectedCount }} 个词</span>
                        </div>
                        <div class="header-right">
                            <el-button v-if="selectedCount > 0" size="small" @click="handleCopySelected">
                                <el-icon>
                                    <DocumentCopy />
                                </el-icon>
                                复制已选蒸馏词
                            </el-button>
                            <el-button size="small" type="primary" @click="handleCopyAll">
                                <el-icon>
                                    <DocumentCopy />
                                </el-icon>
                                复制全部蒸馏词
                            </el-button>
                        </div>
                    </div>

                    <div class="keywords-grid">
                        <div v-for="(keyword, index) in displayedKeywords" :key="index" class="keyword-item"
                            :class="{ selected: keyword.selected }">
                            <div class="keyword-content">
                                <el-checkbox v-model="keyword.selected" class="keyword-checkbox" />
                                <span class="keyword-index">{{ index + 1 }}</span>
                                <span class="keyword-text">{{ getKeywordDisplayText(keyword) }}</span>
                            </div>
                            <el-button class="remove-btn" text @click="removeKeyword(index)">
                                <el-icon>
                                    <Close />
                                </el-icon>
                            </el-button>
                        </div>
                    </div>
                </div>
            </div>

            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitKeywordForm" :loading="submitting"
                    :disabled="selectedCount === 0">
                    保存（已选{{ selectedCount }}个词）
                </el-button>
            </template>
        </el-dialog>

        <!-- 手动批量添加对话框 -->
        <el-dialog v-model="manualBatchDialogVisible" title="手动批量添加" width="600px" class="manual-batch-dialog">
            <div class="batch-content">
                <el-alert type="warning" :closable="false" class="batch-warning">
                    过热的词需要更久的时间出效果，前期建议填写更精准的词
                </el-alert>

                <div class="batch-type-selector">
                    <el-radio-group v-model="batchType">
                        <el-radio label="combine">批量组合添加</el-radio>
                        <el-radio label="main">批量添加主词</el-radio>
                    </el-radio-group>
                </div>

                <!-- 批量组合添加 -->
                <div v-if="batchType === 'combine'" class="combine-section">
                    <div class="input-section">
                        <div class="input-group">
                            <div class="input-header">
                                <span class="input-label">前缀（按回车键换行，一行一个，0-15个）</span>
                                <el-tooltip placement="top">
                                    <template #content>
                                        前缀如：<br>推荐<br>求推荐<br>推荐一下<br>靠谱的<br>口碑好的<br>口碑不错的<br>诚信的<br>专业的<br>实力强的<br>售后完善的
                                    </template>
                                    <el-icon class="help-icon">
                                        <QuestionFilled />
                                    </el-icon>
                                </el-tooltip>
                                <el-button type="text" class="clear-btn" @click="batchForm.prefixes = ''">清空</el-button>
                            </div>
                            <el-input v-model="batchForm.prefixes" type="textarea" :rows="3"
                                placeholder="推荐&#10;求推荐&#10;推荐一下&#10;靠谱的&#10;口碑好的" />
                        </div>

                        <div class="input-group">
                            <div class="input-header">
                                <span class="input-label">主词（必须包含关键词'{{ keywordForm.original_keyword
                                }}'，一行一个，1-30个）</span>
                                <el-button type="text" class="clear-btn"
                                    @click="batchForm.mainWords = ''">清空</el-button>
                            </div>
                            <el-input v-model="batchForm.mainWords" type="textarea" :rows="3"
                                :placeholder="`请输入主词，必须包含你输入的关键词: ${keywordForm.original_keyword}`" />
                        </div>

                        <div class="input-group">
                            <div class="input-header">
                                <span class="input-label">后缀（按回车键换行，一行一个，0-15个）</span>
                                <el-tooltip placement="top">
                                    <template #content>
                                        后缀如：<br>厂家<br>生产厂家<br>源头厂家<br>供应商<br>公司<br>服务商<br>渠道<br>机构<br>方案<br>平台<br>权威平台<br>管理平台
                                    </template>
                                    <el-icon class="help-icon">
                                        <QuestionFilled />
                                    </el-icon>
                                </el-tooltip>
                                <el-button type="text" class="clear-btn" @click="batchForm.suffixes = ''">清空</el-button>
                            </div>
                            <el-input v-model="batchForm.suffixes" type="textarea" :rows="3"
                                placeholder="厂家&#10;生产厂家&#10;源头厂家&#10;供应商&#10;公司" />
                        </div>
                    </div>
                </div>

                <!-- 批量添加主词 -->
                <div v-else class="main-words-section">
                    <div class="input-group">
                        <div class="input-header">
                            <span class="input-label">主词（必须包含关键词'{{ keywordForm.original_keyword }}'，一行一个，1-200个）</span>
                        </div>
                        <el-input v-model="batchForm.mainWordsOnly" type="textarea" :rows="6"
                            :placeholder="`请输入主词，必须包含你输入的关键词: ${keywordForm.original_keyword}`" />
                    </div>
                </div>
            </div>

            <template #footer>
                <el-button @click="manualBatchDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="handleBatchAddConfirm">确定</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import {
    Plus, Edit, Delete, Filter, DocumentAdd, DocumentCopy, Close, QuestionFilled
} from '@element-plus/icons-vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
    name: 'KeywordManagement',
    components: {
        Plus, Edit, Delete, Filter, DocumentAdd, DocumentCopy, Close, QuestionFilled
    },
    data() {
        return {
            loading: false,
            submitting: false,
            distilling: false,

            // 对话框控制
            dialogVisible: false,
            manualBatchDialogVisible: false,
            dialogTitle: '添加搜索词',
            isEditMode: false,
            batchType: 'combine',

            // 关键词类型
            activeKeywordType: 'search',
            keywordTypes: [
                { id: 'search', label: '搜索词' },
                { id: 'qa', label: '问答词' },
                { id: 'brand', label: '品牌词' }
            ],

            // 关键词表单
            keywordForm: {
                id: null,
                package_name: '',
                original_keyword: '',
                industry: 'manufacturing',
                description: '',
                keywords: []
            },

            // 批量表单
            batchForm: {
                prefixes: '',
                mainWords: '',
                suffixes: '',
                mainWordsOnly: ''
            },

            // 关键词列表显示
            displayedKeywords: [],
            selectAll: false,

            // 词包列表
            packageList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('store_user_info') || '{}')
        },
        storeId() {
            return this.userInfo.store_id
        },
        userId() {
            return this.userInfo.id
        },
        selectedCount() {
            return this.displayedKeywords.filter(k => k.selected).length
        }
    },
    watch: {
        displayedKeywords: {
            handler(newVal) {
                const selectedCount = newVal.filter(k => k.selected).length
                this.selectAll = selectedCount > 0 && selectedCount === newVal.length
            },
            deep: true
        }
    },
    mounted() {
        this.fetchPackageList()
    },
    methods: {
        // 获取当前类型标签
        getCurrentTypeLabel() {
            const type = this.keywordTypes.find(t => t.id === this.activeKeywordType)
            return type ? type.label : '关键词'
        },

        // 获取行业标签
        getIndustryLabel(industry) {
            const industries = {
                manufacturing: '制造业',
                service: '服务业'
            }
            return industries[industry] || industry
        },

        // 获取关键词显示文本
        getKeywordDisplayText(keyword) {
            if (typeof keyword === 'string') return keyword
            if (keyword.text) return keyword.text
            if (keyword.question) return keyword.question
            if (keyword.name) return keyword.name
            return JSON.stringify(keyword)
        },

        // 切换关键词类型
        handleKeywordTypeChange(type) {
            this.activeKeywordType = type
            this.fetchPackageList()
        },

        // 打开添加对话框
        async openAddDialog() {
            // 检查词包数量限制
            try {
                const { data } = await axios.post(`${config.BASE_URL}/store/api/keyword_management.php`, {
                    action: 'check_limit',
                    store_id: this.storeId,
                    user_id: this.userId,
                    keyword_type: this.activeKeywordType
                })

                if (data.exceeds_limit) {
                    await ElMessageBox.confirm(
                        `您已添加${data.package_count}个${this.getCurrentTypeLabel()}词包，第一次使用新词包需要至少发20条信息，且不停添加新的词包可能会分散旧词包的效果，建议维护好已添加的词包，通过添加画像的方式去训练新的文章。是否继续添加？`,
                        `添加${this.getCurrentTypeLabel()}`,
                        {
                            confirmButtonText: '继续添加',
                            cancelButtonText: '取消',
                            type: 'warning'
                        }
                    )
                }
            } catch (error) {
                // 用户取消操作
                if (error === 'cancel') return
                console.error('检查词包限制失败:', error)
            }

            this.dialogTitle = `添加${this.getCurrentTypeLabel()}`
            this.isEditMode = false
            this.keywordForm = {
                id: null,
                package_name: '',
                original_keyword: '',
                industry: 'manufacturing',
                description: '',
                keywords: []
            }
            this.displayedKeywords = []
            this.dialogVisible = true
        },

        // 打开编辑对话框
        async openEditDialog(row) {
            try {
                const { data } = await axios.post(`${config.BASE_URL}/store/api/keyword_management.php`, {
                    action: 'get_package_detail',
                    store_id: this.storeId,
                    user_id: this.userId,
                    id: row.id
                })

                if (data.success) {
                    this.dialogTitle = `编辑${this.getCurrentTypeLabel()}`
                    this.isEditMode = true
                    this.keywordForm = {
                        ...data.data,
                        keywords: data.data.keywords || []
                    }
                    this.displayedKeywords = this.keywordForm.keywords.map(kw => ({
                        ...kw,
                        selected: true
                    }))
                    this.dialogVisible = true
                }
            } catch (error) {
                ElMessage.error('获取词包详情失败')
                console.error('获取词包详情错误:', error)
            }
        },

        // AI蒸馏
        async handleDistill() {
            if (!this.keywordForm.original_keyword.trim()) {
                ElMessage.warning('请输入关键词')
                return
            }

            if (this.keywordForm.original_keyword.trim().length < 3) {
                ElMessage.warning('关键词最少需要3个字')
                return
            }

            this.distilling = true
            try {
                const { data } = await axios.post(`${config.BASE_URL}/site/api/keyword_management.php`, {
                    action: 'distill_keywords',
                    store_id: this.storeId,
                    user_id: this.userId,
                    keyword: this.keywordForm.original_keyword.trim(),
                    keyword_type: this.activeKeywordType,
                    industry: this.keywordForm.industry
                })

                if (data.success) {
                    // 将蒸馏结果添加到显示列表
                    const newKeywords = data.data.map(kw => ({
                        ...(typeof kw === 'object' ? kw : { text: kw }),
                        selected: true
                    }))
                    this.displayedKeywords = [...this.displayedKeywords, ...newKeywords]
                    ElMessage.success(`蒸馏完成，生成${newKeywords.length}个关键词`)
                } else {
                    ElMessage.error(data.error || '蒸馏失败')
                }
            } catch (error) {
                ElMessage.error('蒸馏失败，请重试')
                console.error('蒸馏错误:', error)
            } finally {
                this.distilling = false
            }
        },

        // 打开手动批量对话框
        openManualBatchDialog() {
            if (!this.keywordForm.original_keyword.trim()) {
                ElMessage.warning('请先输入关键词')
                return
            }

            this.batchType = 'combine'
            this.batchForm = {
                prefixes: '',
                mainWords: '',
                suffixes: '',
                mainWordsOnly: ''
            }
            this.manualBatchDialogVisible = true
        },

        // 批量添加确认
        handleBatchAddConfirm() {
            let newKeywords = []

            if (this.batchType === 'combine') {
                // 批量组合添加逻辑
                const prefixes = this.batchForm.prefixes.split('\n').filter(p => p.trim())
                const mainWords = this.batchForm.mainWords.split('\n').filter(m => m.trim())
                const suffixes = this.batchForm.suffixes.split('\n').filter(s => s.trim())

                // 生成组合关键词
                for (const mainWord of mainWords) {
                    // 检查主词是否包含核心关键词
                    if (!mainWord.includes(this.keywordForm.original_keyword)) {
                        ElMessage.warning(`主词"${mainWord}"必须包含核心关键词"${this.keywordForm.original_keyword}"`)
                        continue
                    }

                    if (prefixes.length === 0 && suffixes.length === 0) {
                        newKeywords.push(mainWord)
                    } else {
                        for (const prefix of prefixes.length ? prefixes : ['']) {
                            for (const suffix of suffixes.length ? suffixes : ['']) {
                                const combined = prefix + mainWord + suffix
                                if (combined.trim()) {
                                    newKeywords.push(combined.trim())
                                }
                            }
                        }
                    }
                }
            } else {
                // 批量添加主词
                newKeywords = this.batchForm.mainWordsOnly.split('\n')
                    .filter(m => m.trim())
                    .filter(m => m.includes(this.keywordForm.original_keyword))
            }

            // 去重
            newKeywords = [...new Set(newKeywords)]

            if (newKeywords.length === 0) {
                ElMessage.warning('没有生成有效关键词')
                return
            }

            // 添加到显示列表
            const formattedKeywords = newKeywords.map(kw => ({
                ...(typeof kw === 'string' ? { text: kw } : kw),
                selected: true
            }))
            this.displayedKeywords = [...this.displayedKeywords, ...formattedKeywords]

            this.manualBatchDialogVisible = false
            ElMessage.success(`成功添加${newKeywords.length}个关键词`)
        },

        // 全选/取消全选
        handleSelectAll(checked) {
            this.displayedKeywords.forEach(keyword => {
                keyword.selected = checked
            })
        },

        // 复制已选关键词
        handleCopySelected() {
            const selectedTexts = this.displayedKeywords
                .filter(k => k.selected)
                .map(k => this.getKeywordDisplayText(k))
                .join('\n')

            this.copyToClipboard(selectedTexts)
            ElMessage.success('已复制选中的关键词')
        },

        // 复制全部关键词
        handleCopyAll() {
            const allTexts = this.displayedKeywords
                .map(k => this.getKeywordDisplayText(k))
                .join('\n')

            this.copyToClipboard(allTexts)
            ElMessage.success('已复制全部关键词')
        },

        // 复制到剪贴板
        copyToClipboard(text) {
            navigator.clipboard.writeText(text).catch(() => {
                const textArea = document.createElement('textarea')
                textArea.value = text
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand('copy')
                document.body.removeChild(textArea)
            })
        },

        // 移除关键词
        removeKeyword(index) {
            this.displayedKeywords.splice(index, 1)
        },

        // 提交关键词表单
        async submitKeywordForm() {
            if (!this.keywordForm.original_keyword.trim()) {
                ElMessage.warning('请输入关键词')
                return
            }

            if (this.selectedCount === 0) {
                ElMessage.warning('请至少选择一个关键词')
                return
            }

            this.submitting = true
            try {
                const selectedKeywords = this.displayedKeywords
                    .filter(k => k.selected)
                    .map(k => {
                        // eslint-disable-next-line no-unused-vars
                        const { selected, ...keyword } = k
                        return keyword
                    })

                const payload = {
                    store_id: this.storeId,
                    user_id: this.userId,
                    package_name: this.keywordForm.original_keyword,
                    keyword_type: this.activeKeywordType,
                    original_keyword: this.keywordForm.original_keyword,
                    industry: this.keywordForm.industry,
                    description: this.keywordForm.description,
                    keywords_json: JSON.stringify(selectedKeywords)
                }

                const action = this.isEditMode ? 'update_package' : 'add_package'
                if (this.isEditMode) {
                    payload.id = this.keywordForm.id
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/store/api/keyword_management.php`,
                    { action, ...payload }
                )

                if (data.success) {
                    ElMessage.success(this.isEditMode ? '更新成功' : '添加成功')
                    this.dialogVisible = false
                    this.fetchPackageList()
                } else {
                    ElMessage.error(data.error || '操作失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '操作失败'
                ElMessage.error(`操作失败: ${errorMsg}`)
                console.error('提交关键词表单错误:', error)
            } finally {
                this.submitting = false
            }
        },

        // 删除关键词包
        async handleDelete(id) {
            try {
                await ElMessageBox.confirm('确定要删除该关键词包吗？', '警告', {
                    confirmButtonText: '确认删除',
                    cancelButtonText: '取消',
                    type: 'warning'
                })

                const { data } = await axios.post(`${config.BASE_URL}/store/api/keyword_management.php`, {
                    action: 'delete_package',
                    store_id: this.storeId,
                    user_id: this.userId,
                    id: id
                })

                if (data.success) {
                    ElMessage.success('删除成功')
                    this.fetchPackageList()
                } else {
                    ElMessage.error(data.error || '删除失败')
                }
            } catch (error) {
                if (error !== 'cancel') {
                    ElMessage.error('删除失败')
                }
            }
        },

        // 获取词包列表
        async fetchPackageList() {
            this.loading = true
            try {
                const { data } = await axios.post(`${config.BASE_URL}/store/api/keyword_management.php`, {
                    action: 'list_packages',
                    store_id: this.storeId,
                    user_id: this.userId,
                    keyword_type: this.activeKeywordType,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                })

                if (data.data) {
                    this.packageList = data.data
                    this.pagination.total = data.pagination?.total || 0
                }
            } catch (error) {
                ElMessage.error('获取词包列表失败')
                console.error('获取词包列表错误:', error)
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<!-- 样式部分保持不变，与之前相同 -->
<style scoped lang="scss">
.keyword-management-container {
    min-height: calc(100vh - 160px);
    background: transparent;
}

.main-content {
    background: hsla(0, 0%, 100%, .1);
    border-radius: 16px;
    padding: 24px;
    backdrop-filter: blur(10px);
}

.button-group {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.main-button {
    padding: 12px 24px;
    background: hsla(0, 0%, 100%, .1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: #e0e6ff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #ff7048;
        border-color: #ff7048;
        transform: translateY(-2px);
    }

    &.active {
        background: #ff7048;
        border-color: #ff7048;
    }
}

.action-buttons {
    margin-bottom: 24px;
}

.add-button {
    background: #ff7048;
    border-color: #ff7048;

    &:hover {
        background: #e55a3a;
        border-color: #e55a3a;
    }
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 对话框样式 */
.dialog-content-wrapper {
    max-height: 70vh;
    overflow-y: auto;
    padding-right: 10px;
}

.dialog-header {
    margin-bottom: 20px;
}

.dialog-main-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: bold;
    margin: 0;

    .dialog-subtitle {
        font-size: 14px;
        font-weight: normal;
        color: #a0a8b8;
    }

    .warning-text {
        color: #ff7048;
        font-size: 14px;
        margin-left: 10px;
    }
}

.keyword-input-row {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    align-items: center;
    flex-wrap: wrap;

    .keyword-input {
        flex: 1;
        min-width: 200px;
    }

    .industry-select {
        width: 120px;
    }

    .distill-button {
        background: #ff7048;
        border-color: #ff7048;
    }

    .manual-batch-button {
        border-color: #ff7048;
        color: #ff7048;

        &:hover {
            background: rgba(255, 112, 72, 0.1);
        }
    }
}

.description-row {
    margin-bottom: 20px;

    .description-input {
        width: 100%;
    }
}

.edit-keyword-display {
    background: rgba(255, 112, 72, 0.1);
    border: 1px solid rgba(255, 112, 72, 0.3);
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 20px;

    .keyword-display {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;

        .label {
            color: #ffffff;
            font-weight: 500;
        }

        .value {
            color: #ff7048;
            font-weight: bold;
        }
    }

    .edit-tip {
        color: #a0a8b8;
        font-size: 12px;
    }
}

.keywords-list-section {
    border: 1px solid rgba(255, 112, 72, 0.3);
    border-radius: 8px;
    overflow: hidden;
}

.list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: rgba(255, 112, 72, 0.1);
    border-bottom: 1px solid rgba(255, 112, 72, 0.3);

    .header-left {
        display: flex;
        align-items: center;
        gap: 16px;

        .selected-count {
            color: #ff7048;
            font-weight: 500;
        }
    }
}

.keywords-grid {
    max-height: 300px;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.keyword-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    transition: all 0.3s ease;

    &:hover {
        border-color: #ff7048;
    }

    &.selected {
        border-color: #ff7048;
        background: rgba(255, 112, 72, 0.1);
    }

    .keyword-content {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;

        .keyword-checkbox {
            margin-right: 8px;
        }

        .keyword-index {
            color: #a0a8b8;
            font-size: 12px;
            min-width: 20px;
        }

        .keyword-text {
            color: #e0e6ff;
            flex: 1;
        }
    }

    .remove-btn {
        color: #a0a8b8;

        &:hover {
            color: #ff7048;
        }
    }
}

/* 手动批量对话框样式 */
.batch-content {
    .batch-warning {
        margin-bottom: 20px;
        background: rgba(255, 112, 72, 0.1);
        border: 1px solid rgba(255, 112, 72, 0.3);
        color: #ff7048;
    }

    .batch-type-selector {
        margin-bottom: 20px;

        :deep(.el-radio) {
            color: #e0e6ff;
        }
    }

    .input-group {
        margin-bottom: 20px;

        .input-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;

            .input-label {
                color: #e0e6ff;
                font-weight: 500;
            }

            .help-icon {
                color: #ff7048;
                cursor: help;
            }

            .clear-btn {
                color: #ff7048;
                padding: 0;
                margin-left: auto;
            }
        }
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .keyword-management-container {
        padding: 16px;
    }

    .button-group {
        gap: 12px;

        .main-button {
            padding: 10px 20px;
            font-size: 13px;
        }
    }

    .keyword-input-row {
        flex-direction: column;
        align-items: stretch;

        .keyword-input,
        .industry-select {
            width: 100% !important;
        }
    }

    .list-header {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }
}
</style>

<style>
/* 全局对话框样式 */
.keyword-dialog .el-dialog,
.manual-batch-dialog .el-dialog {
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 112, 72, 0.3);
    border-radius: 16px;
}

.keyword-dialog .el-dialog__header,
.manual-batch-dialog .el-dialog__header {
    margin: 0 !important;
    padding: 20px !important;
    border-bottom: 1px solid rgba(255, 112, 72, 0.2);
}

.keyword-dialog .el-dialog__title,
.manual-batch-dialog .el-dialog__title {
    color: #ffffff !important;
    font-weight: 600;
    font-size: 18px;
}

.keyword-dialog .el-dialog__body,
.manual-batch-dialog .el-dialog__body {
    padding: 20px !important;
    background: transparent !important;
}

.keyword-dialog .el-dialog__footer,
.manual-batch-dialog .el-dialog__footer {
    border-top: 1px solid rgba(255, 112, 72, 0.2) !important;
    padding: 16px 20px !important;
    margin: 0 !important;
}

/* 表单元素样式 */
.keyword-dialog .el-textarea__inner,
.manual-batch-dialog .el-textarea__inner {
    color: #ffffff !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.keyword-dialog .el-input__inner,
.manual-batch-dialog .el-input__inner {
    color: #ffffff !important;

}

.keyword-dialog .el-input__inner::placeholder,
.keyword-dialog .el-textarea__inner::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
}

/* 复选框样式 */
.keyword-dialog .el-checkbox__label {
    color: #e0e6ff !important;
}

.keyword-dialog .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: #ff7048 !important;
    border-color: #ff7048 !important;
}

/* 单选按钮样式 */
.manual-batch-dialog .el-radio__input.is-checked .el-radio__inner {
    background-color: #ff7048 !important;
    border-color: #ff7048 !important;
}

.manual-batch-dialog .el-radio__input.is-checked+.el-radio__label {
    color: #ff7048 !important;
}

/* 按钮样式 */
.keyword-dialog .el-button,
.manual-batch-dialog .el-button {
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.keyword-dialog .el-button--primary,
.manual-batch-dialog .el-button--primary {
    background: #ff7048 !important;
    border-color: #ff7048 !important;
}

.keyword-dialog .el-button--primary:hover,
.manual-batch-dialog .el-button--primary:hover {
    background: #e55a3a !important;
    border-color: #e55a3a !important;
    transform: translateY(-1px);
}

/* 滚动条样式 */
.keywords-grid::-webkit-scrollbar {
    width: 6px;
}

.keywords-grid::-webkit-scrollbar-track {
    background: rgba(255, 112, 72, 0.1);
    border-radius: 3px;
}

.keywords-grid::-webkit-scrollbar-thumb {
    background: #ff7048;
    border-radius: 3px;
}

.keywords-grid::-webkit-scrollbar-thumb:hover {
    background: #e55a3a;
}

.dialog-content-wrapper::-webkit-scrollbar {
    width: 6px;
}

.dialog-content-wrapper::-webkit-scrollbar-track {
    background: rgba(255, 112, 72, 0.1);
    border-radius: 3px;
}

.dialog-content-wrapper::-webkit-scrollbar-thumb {
    background: #ff7048;
    border-radius: 3px;
}

.dialog-content-wrapper::-webkit-scrollbar-thumb:hover {
    background: #e55a3a;
}
</style>